import React, { useEffect } from 'react';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { useAuth } from '../../../contexts/AuthContext';
import GlassCard from '../../../components/ui/GlassCard';
import '../OnboardingWizard.css';

const GOALS = [
  {
    id: 'first_job',
    icon: '🎯',
    title: 'Looking for my first job/internship',
    description: 'Just starting out and need guidance on the job search process',
  },
  {
    id: 'new_opportunities',
    icon: '🚀',
    title: 'Exploring new opportunities',
    description: 'Currently employed but open to better positions',
  },
  {
    id: 'interview_prep',
    icon: '💡',
    title: 'Preparing for upcoming interviews',
    description: 'Have interviews lined up and want to practice',
  },
  {
    id: 'keeping_options_open',
    icon: '🌟',
    title: 'Keeping my options open',
    description: 'Not actively searching but want to stay prepared',
  },
];

function WelcomeGoals() {
  const { data, updateStepData } = useOnboarding();
  const { user } = useAuth();

  // Pre-fill name from Google OAuth if not already set
  useEffect(() => {
    if (!data.full_name) {
      const oauthName = user?.user_metadata?.full_name ||
                        user?.user_metadata?.name ||
                        '';
      if (oauthName) {
        updateStepData({ full_name: oauthName });
      }
    }
  }, [user, data.full_name, updateStepData]);

  // Get first name for greeting
  const firstName = data.full_name?.split(' ')[0] ||
                    user?.user_metadata?.full_name?.split(' ')[0] ||
                    '';

  const handleSelectGoal = (goalId) => {
    updateStepData({ primary_goal: goalId });
  };

  const handleNameChange = (e) => {
    updateStepData({ full_name: e.target.value });
  };

  return (
    <div className="step-content">
      <div className="step-header">
        <h1 className="step-title">
          Welcome to Careerly{firstName ? `, ${firstName}` : ''}!
        </h1>
        <p className="step-subtitle">
          Let's personalize your experience. First, tell us a bit about yourself.
        </p>
      </div>

      {/* Name Input */}
      <div className="form-group" style={{ marginBottom: 'var(--space-2xl)' }}>
        <label className="form-label">What's your name?</label>
        <input
          type="text"
          className="form-input"
          placeholder="Enter your full name"
          value={data.full_name || ''}
          onChange={handleNameChange}
          autoFocus
        />
        <p className="form-hint">This is how we'll address you throughout the app.</p>
      </div>

      {/* Goals Selection */}
      <div className="form-group">
        <label className="form-label">What brings you here today?</label>
      </div>

      <div className="goals-grid">
        {GOALS.map((goal) => (
          <GlassCard
            key={goal.id}
            hoverable
            onClick={() => handleSelectGoal(goal.id)}
            className={`goal-card ${data.primary_goal === goal.id ? 'selected' : ''}`}
          >
            <div className="goal-icon">{goal.icon}</div>
            <div className="goal-info">
              <h3 className="goal-title">{goal.title}</h3>
              <p className="goal-description">{goal.description}</p>
            </div>
            <div className="goal-check">
              {data.primary_goal === goal.id && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="var(--green-500)"/>
                  <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
          </GlassCard>
        ))}
      </div>

      <p className="step-note">
        This helps us tailor job recommendations and interview prep to your needs.
      </p>
    </div>
  );
}

export default WelcomeGoals;
